import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {ColumnProps} from 'components/StickyHeadTable/StickyHeadTable';
import {GET_LEAVE_REQUEST_BY_APPROVAL} from 'graphql/absences/Querys';
import GqlTableController from 'controllers/GqlTableController/GqlTableController';
import {
  LEAVE_STATUS_APPROVED,
  LEAVE_STATUS_IN_PROCESS,
  LEAVE_STATUS_PENDING,
  LEAVE_STATUS_REJECTED,
  LEAVE_STATUS_CANCELLED,
  LEAVE_STATUS_APPROVAL_CANCELLED,
  LEAVE_STATUS_USER_APPROVAL_CANCELLED,
  LeaveRequestFilterData,
  LeaveStatus,
  leaveStatusLabels,
} from 'models/leaveRequest';
import BlankCardWithTabs from 'components/BlankCardWithTabs/BlankCardWithTabs';
import Chip from 'components/Chip/Chip';
import {DocumentNode} from '@apollo/client';
import ViewTitle from 'viewComponents/ViewTitle/ViewTitle';
import Status from 'components/Status/Status';
import {LeavesRoutes, PrivateRoutes} from 'navigation/routes';
import RoleGuard from 'commons/guards/roleGuard';
import Button from 'components/button/Button';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LeaveRequestFilterModal from 'viewComponents/LeaveRequestFilterModal/LeaveRequestFilterModal';
import {formatDateByLocale} from 'commons/formatDate';
import {useTranslateCatalog} from 'commons/hooks/useTranslateCatalog';

interface PendingApprovalsProps {
  approvalId: string;
  statusLeave?: LeaveStatus[];
  statusApproval?: LeaveStatus[];
  onlyInOrder: boolean;
  sort?: Map<string, string>;
  isAdmin?: boolean;
  filter?: Omit<LeaveRequestFilterData, 'status'> | null;
}

export const PendingApprovalsTable: FC = () => {
  const {id} = useParams<{id: string}>();
  const {t, i18n} = useTranslation();
  const {translateCatalog} = useTranslateCatalog();
  const [filter, setFilter] = React.useState<
    LeaveRequestFilterData | undefined
  >();
  const [selected, setSelected] = React.useState(0);
  const [isFilterFormVisible, setIsFilterFormVisible] = React.useState(false);
  const navigate = useNavigate();
  const columns: ColumnProps[] = [
    {
      id: 'userName',
      label: t('pendingApprovalsTable.name'),
    },
    {
      id: 'dates',
      label: t('pendingApprovalsTable.dates'),
    },
    {
      id: 'createdAt',
      label: t('pendingApprovalsTable.createdAt'),
      render: (value: string) =>
        formatDateByLocale(value, i18n.language as 'en' | 'es' | 'fr' | 'pt'),
    },
    {
      id: 'typeOfLeave',
      label: t('pendingApprovalsTable.typeOfLeave'),
      render: value =>
        translateCatalog(value)?.description || value?.description || '',
    },
    {
      id: 'daysOff',
      label: t('pendingApprovalsTable.numberOfDays'),
    },
    {
      id: 'status',
      label: t('pendingApprovalsTable.status'),
      render: value => {
        return <Status label={value} />;
      },
    },
    {
      id: 'countryCode',
      label: t('pendingApprovalsTable.country'),
      render: value => {
        return value || '';
      },
    },
    {
      id: 'assignationType',
      label: t('pendingApprovalsTable.assignation'),
    },
  ];
  // eslint-disable-next-line
  const mapData = (data: any) => ({
    dates: `${formatDateByLocale(
      data.startDate,
      i18n.language as 'en' | 'es' | 'fr' | 'pt'
    )} - ${formatDateByLocale(
      data.endDate,
      i18n.language as 'en' | 'es' | 'fr' | 'pt'
    )}`,
    ...data,
  });

  const hashFilter = (): string => {
    if (filter) {
      return JSON.stringify(filter);
    }
    return '';
  };

  const handleTabChange = (index: number) => {
    setSelected(index);
    if (index !== 1) {
      setIsFilterFormVisible(false);
    }
  };

  const handleToggleFilters = () => {
    setIsFilterFormVisible(previousState => !previousState);
  };

  const handleClearFilters = () => {
    setFilter(undefined);
  };

  const formatFilterDate = (value?: Date | string | null) => {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    return formatDateByLocale(date, i18n.language as 'en' | 'es' | 'fr' | 'pt');
  };

  const getAppliedFilterSummary = () => {
    if (!filter) {
      return [];
    }

    const summary: {id: string; text: string}[] = [];

    if (filter.name) {
      summary.push({
        id: 'name',
        text: `${t('pendingApprovalsTable.name')}: ${filter.name}`,
      });
    }

    if (filter.status) {
      summary.push({
        id: 'status',
        text: `${t('pendingApprovalsTable.status')}: ${t(
          leaveStatusLabels(String(filter.status))
        )}`,
      });
    }

    if (filter.countryCode) {
      summary.push({
        id: 'countryCode',
        text: `${t('pendingApprovalsTable.country')}: ${filter.countryCode}`,
      });
    }

    if (filter.leaveRequest?.startDate && filter.leaveRequest?.endDate) {
      summary.push({
        id: 'leaveRequest',
        text: `${t('pendingApprovalsTable.requestDate')}: ${formatFilterDate(
          filter.leaveRequest.startDate
        )} - ${formatFilterDate(filter.leaveRequest.endDate)}`,
      });
    }

    if (filter.leavePeriod?.startDate && filter.leavePeriod?.endDate) {
      summary.push({
        id: 'leavePeriod',
        text: `${t('pendingApprovalsTable.leavePeriod')}: ${formatFilterDate(
          filter.leavePeriod.startDate
        )} - ${formatFilterDate(filter.leavePeriod.endDate)}`,
      });
    }

    return summary;
  };

  const adjustRequestDateRange = (filter: LeaveRequestFilterData) => {
    if (filter.leaveRequest?.endDate) {
      const endDate = new Date(filter.leaveRequest.endDate);
      // add a day to endDate
      endDate.setDate(endDate.getDate() + 1);
      filter.leaveRequest.endDate = endDate;
    }
  };

  const leaveRequestView = (
    document: DocumentNode,
    placeholder: string,
    withArgs: PendingApprovalsProps
  ) => {
    return (
      <GqlTableController
        rowProps={{
          // eslint-disable-next-line
          onClick: (values: any) =>
            navigate(
              `/${PrivateRoutes.PRIVATE}/leaves/${LeavesRoutes.REQUEST_DETAIL}/${values.id}`
            ),
        }}
        columns={columns}
        placeholder={placeholder}
        mapData={mapData}
        updateTrigger={hashFilter()}
        queryConfiguration={{
          query: document,
          rootField: 'leaveRequestsByApproval',
          queryVariables: (page, size) => ({
            ...withArgs,
            pageParams: {
              page: page || 1,
              size,
            },
          }),
        }}
      />
    );
  };

  const pendingApprovalView = leaveRequestView(
    GET_LEAVE_REQUEST_BY_APPROVAL,
    t('pendingApprovalsTable.noPendingRequests'),
    {
      approvalId: id || '',
      statusLeave: [LEAVE_STATUS_PENDING, LEAVE_STATUS_IN_PROCESS],
      statusApproval: [LEAVE_STATUS_PENDING],
      onlyInOrder: true,
      isAdmin: false,
      filter: null,
    }
  );

  const getHistoryView = (isAdmin: boolean) => {
    return leaveRequestView(
      GET_LEAVE_REQUEST_BY_APPROVAL,
      t('pendingApprovalsTable.noDataAvailable'),
      {
        approvalId: id || '',
        statusApproval: [
          LEAVE_STATUS_APPROVED,
          LEAVE_STATUS_REJECTED,
          LEAVE_STATUS_PENDING,
        ],
        statusLeave: [
          LEAVE_STATUS_APPROVED,
          LEAVE_STATUS_REJECTED,
          LEAVE_STATUS_IN_PROCESS,
          LEAVE_STATUS_PENDING,
          LEAVE_STATUS_CANCELLED,
          LEAVE_STATUS_APPROVAL_CANCELLED,
          LEAVE_STATUS_USER_APPROVAL_CANCELLED,
        ],
        onlyInOrder: false,
        isAdmin: isAdmin,
        filter: null,
      }
    );
  };
  const getFilteredView = (isAdmin: boolean) => {
    const filterToBeSent = {...filter};
    adjustRequestDateRange(filterToBeSent);
    delete filterToBeSent.status;
    return leaveRequestView(
      GET_LEAVE_REQUEST_BY_APPROVAL,
      t('pendingApprovalsTable.noDataAvailable'),
      {
        approvalId: id || '',
        statusLeave: filter?.status
          ? [filter?.status]
          : [
              LEAVE_STATUS_APPROVED,
              LEAVE_STATUS_REJECTED,
              LEAVE_STATUS_IN_PROCESS,
              LEAVE_STATUS_PENDING,
              LEAVE_STATUS_CANCELLED,
              LEAVE_STATUS_APPROVAL_CANCELLED,
              LEAVE_STATUS_USER_APPROVAL_CANCELLED,
            ],
        statusApproval: [
          LEAVE_STATUS_APPROVED,
          LEAVE_STATUS_REJECTED,
          LEAVE_STATUS_PENDING,
        ],
        onlyInOrder: false,
        isAdmin: isAdmin,
        filter: filterToBeSent,
      }
    );
  };

  const appliedFilterSummary = getAppliedFilterSummary();

  return (
    <div className="PalowansOnBreakTable w-full flex flex-col flex-wrap pt-12">
      <ViewTitle>{t('pendingApprovalsTable.title')}</ViewTitle>
      <RoleGuard
        getRole
        render={({isAdmin}) => {
          return (
            <>
              {selected === 1 && (
                <div className="mt-4 mb-2 flex justify-end gap-2">
                  <Button
                    variant={isFilterFormVisible ? 'contained' : 'outlined'}
                    text={
                      isFilterFormVisible
                        ? t('pendingApprovalsTable.hideFilters')
                        : t('pendingApprovalsTable.showFilters')
                    }
                    startIcon={<FilterAltIcon />}
                    onClick={handleToggleFilters}
                  />
                  <Button
                    variant="outlined"
                    text={t('pendingApprovalsTable.clearFilters')}
                    onClick={handleClearFilters}
                    disabled={!filter}
                  />
                </div>
              )}

              {selected === 1 && isFilterFormVisible && (
                <LeaveRequestFilterModal
                  inline
                  onSubmit={data => {
                    setFilter(data);
                    setIsFilterFormVisible(false);
                  }}
                />
              )}

              {selected === 1 && appliedFilterSummary.length > 0 && (
                <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="mb-2 text-sm font-semibold text-gray-700">
                    {t('pendingApprovalsTable.appliedFilters')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appliedFilterSummary.map(item => (
                      <Chip
                        key={item.id}
                        id={item.id}
                        text={item.text}
                        size="small"
                        color="primary"
                      />
                    ))}
                  </div>
                </div>
              )}

              <BlankCardWithTabs
                className="h-full mt-4"
                tabs={[
                  {
                    label: t('pendingApprovalsTable.requestsToApprove'),
                    onChange: handleTabChange,
                    view: pendingApprovalView,
                  },
                  {
                    label: t('pendingApprovalsTable.requestHistory'),
                    onChange: handleTabChange,
                    view: filter
                      ? getFilteredView(isAdmin)
                      : getHistoryView(isAdmin),
                  },
                ]}
              />
            </>
          );
        }}
      />
    </div>
  );
};
